import { createElement } from "react";
import { createTheme } from "@mui/material/styles";
import { green, grey, orange, red, yellow } from "@mui/material/colors";
import Icon from "@/components/common/Icon";

// The DatePicker's "choose date" adornment uses MUI X's own Material calendar by
// default — swap it for the DS Lucide `calendar` so it matches every other icon.
// createElement (not JSX) keeps this config file JSX-free.
const OpenPickerCalendarIcon = () =>
  createElement(Icon, { name: "calendar", size: 20 });

const theme = createTheme({
  palette: {
    primary: {
      main: "#295b54",
      dark: "#1f4741",
      light: "#3d7972",
      contrastText: "#fff",
    },
    secondary: {
      main: "#2a81a0",
      dark: "#18576e",
      light: "#9cc4d2",
      contrastText: "#fff",
    },
    success: { main: "#3d9670", light: "#60be94", contrastText: "#fff" },
    warning: { main: "#b88a2f", contrastText: "#fff" },
    error: { main: "#b14a32", contrastText: "#fff" },
    info: { main: "#2a81a0", contrastText: "#fff" },
    text: {
      primary: "#2f2f2c",
      secondary: "#6e6e66",
      disabled: "#9a9a92",
    },
    background: {
      default: "#fafaf8",
      paper: "#ffffff",
    },
    divider: "#e7e7e3",
    // Existing forecast-dashboard colours, shared with the analysis map.
    targetProgress: {
      farBelow: {
        main: red[400],
        surface: red[50],
        border: red[300],
        text: red[700],
      },
      below: {
        main: orange[400],
        surface: orange[50],
        border: orange[400],
        text: orange[800],
      },
      near: {
        main: yellow[600],
        surface: yellow[50],
        border: yellow[600],
        text: yellow[900],
      },
      reached: {
        main: green[400],
        surface: green[50],
        border: green[400],
        text: green[800],
      },
      unavailable: {
        main: grey[400],
        surface: grey[100],
        border: grey[300],
        text: grey[600],
      },
    },
  },
  typography: {
    fontFamily:
      "'Geist Sans', system-ui, -apple-system, 'Segoe UI', sans-serif",
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    // Display slots — Nunito
    h1: {
      fontFamily: "'Nunito', system-ui, sans-serif",
      fontWeight: 700,
      fontSize: "clamp(40px, 5vw, 64px)",
      lineHeight: 1.05,
      letterSpacing: "-0.02em",
    },
    h2: {
      fontFamily: "'Nunito', system-ui, sans-serif",
      fontWeight: 600,
      fontSize: "clamp(32px, 4vw, 48px)",
      lineHeight: 1.1,
      letterSpacing: "-0.02em",
    },
    // UI headings — Geist Sans
    h3: {
      fontWeight: 600,
      fontSize: 24,
      lineHeight: 1.25,
      letterSpacing: "-0.01em",
    },
    h4: {
      fontWeight: 600,
      fontSize: 20,
      lineHeight: 1.25,
      letterSpacing: "-0.01em",
    },
    h5: { fontWeight: 600, fontSize: 16, lineHeight: 1.3 },
    h6: { fontWeight: 500, fontSize: 14, lineHeight: 1.3 },
    button: { textTransform: "none", fontWeight: 500, letterSpacing: 0 },
    // Eyebrow / overline — uppercase, tracked
    overline: {
      fontWeight: 500,
      fontSize: 12,
      lineHeight: 1.2,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: "#6e6e66",
    },
    // Form / inline labels
    label: { fontWeight: 500, fontSize: 13, lineHeight: 1.2, color: "#2f2f2c" },
    labelSm: {
      fontWeight: 500,
      fontSize: 12,
      lineHeight: 1.2,
      color: "#6e6e66",
    },
    // Numeric readouts — tabular figures
    metric: {
      fontWeight: 500,
      fontSize: 30,
      lineHeight: 1.1,
      letterSpacing: "-0.02em",
      fontVariantNumeric: "tabular-nums",
    },
    metricLg: {
      fontFamily: "'Nunito', system-ui, sans-serif",
      fontWeight: 700,
      fontSize: 64,
      lineHeight: 1,
      letterSpacing: "-0.02em",
      fontVariantNumeric: "tabular-nums",
    },
  },
  // Default component radius (design system: 6px). Components that need a
  // different radius (pill chips, small tooltips) set it explicitly.
  shape: { borderRadius: 6 },

  // Calm, cool-neutral shadow scale (teal-tinted, never black). Replaces MUI's
  // default 25-step black elevation. Levels 1–4 are the DS scale; everything
  // above 4 reuses the float shadow so deep elevations don't muddy the palette.
  //   1 → inputs/buttons at rest   2 → cards that lift
  //   3 → popovers/menus/panels    4+ → modals & toasts (float)
  shadows: [
    "none",
    "0 1px 0 rgba(17,38,36,0.04), 0 1px 2px rgba(17,38,36,0.04)",
    "0 2px 4px rgba(17,38,36,0.05), 0 1px 1px rgba(17,38,36,0.03)",
    "0 8px 24px -8px rgba(17,38,36,0.12), 0 2px 6px rgba(17,38,36,0.04)",
    "0 24px 64px -24px rgba(17,38,36,0.25)",
    ...Array(20).fill("0 24px 64px -24px rgba(17,38,36,0.25)"),
  ],

  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          label: "span",
          labelSm: "span",
          metric: "p",
          metricLg: "p",
        },
      },
    },
    // -- Buttons: 36px, 6px radius, no elevation, sentence case --
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          height: 36,
          padding: "0 14px",
          borderRadius: 6,
          textTransform: "none",
          fontWeight: 500,
          boxShadow: "none",
          transition: "background 120ms cubic-bezier(.2,.6,.2,1)",
          "&:focus-visible": { boxShadow: "0 0 0 3px rgba(42,129,160,0.22)" },
        },
        sizeSmall: { height: 28, padding: "0 10px", fontSize: 13 },
        sizeLarge: { height: 44, padding: "0 20px", fontSize: 15 },
        containedPrimary: {
          backgroundColor: "#295b54",
          "&:hover": { backgroundColor: "#1f4741" },
        },
        outlined: {
          borderColor: "#cfcfc9",
          color: "#2f2f2c",
          backgroundColor: "#ffffff",
          "&:hover": { backgroundColor: "#f3f3f0", borderColor: "#cfcfc9" },
        },
        text: {
          color: "#2f2f2c",
          "&:hover": { backgroundColor: "#f3f3f0" },
        },
      },
      variants: [
        {
          // Destructive: quiet red label + neutral border, never filled red
          props: { variant: "destructive" },
          style: {
            backgroundColor: "transparent",
            color: "#b14a32",
            border: "1px solid #cfcfc9",
            "&:hover": { backgroundColor: "#fbeae3", borderColor: "#cfcfc9" },
          },
        },
      ],
    },

    // -- IconButton --
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          color: "#6e6e66",
          "&:hover": { backgroundColor: "#f3f3f0" },
        },
      },
    },

    // -- Inputs: 36px, 6px radius, ink-300 border, teal focus ring --
    // Default every form control to small so inline FormControl+Select pairs
    // match sibling TextFields without restating size at each call site.
    MuiTextField: {
      defaultProps: { size: "small", variant: "outlined" },
    },
    MuiFormControl: {
      defaultProps: { size: "small" },
    },
    MuiSelect: {
      defaultProps: { size: "small" },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: "#ffffff",
          borderRadius: 6,
          fontSize: 14,
          "& fieldset": { borderColor: "#cfcfc9" },
          "&:hover fieldset": { borderColor: "#9a9a92" },
          // Teal ring on focus — but not when the field is in error.
          "&.Mui-focused:not(.Mui-error)": {
            boxShadow: "0 0 0 3px rgba(42,129,160,0.22)",
          },
          "&.Mui-focused:not(.Mui-error) fieldset": {
            borderColor: "#2a81a0",
            borderWidth: 1,
          },
          // Error keeps the danger border at 1px (no teal ring).
          "&.Mui-error fieldset": { borderColor: "#b14a32" },
          "&.Mui-focused.Mui-error fieldset": { borderWidth: 1 },
        },
        // Size small inputs/selects to ~36px via symmetric vertical padding.
        // Padding-based (not fixed height) so <Select> text stays centred and
        // keeps room for the dropdown arrow; works for multiline too.
        input: {
          "&.MuiInputBase-inputSizeSmall": {
            paddingTop: 7,
            paddingBottom: 7,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: 13,
          fontWeight: 500,
          color: "#2f2f2c",
          "&.Mui-focused": { color: "#2a81a0" },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: "#9a9a92",
          "&.Mui-checked": { color: "#295b54" },
        },
      },
    },
    // -- Helper / hint text: 11px muted, danger on error --
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: 11,
          color: "#6e6e66",
          "&.Mui-error": { color: "#b14a32" },
        },
      },
    },

    // -- Chip = status badge: pill, 22px, soft status-colour FILL --
    // Only the FILLED variant is the DS status badge (soft tint, no border).
    // Status chips in code use filled (default). Outlined is left distinct (MUI
    // default) as the filter "off" state, migrating to ToggleButton in the
    // search/filter pass. primary/secondary remap -> final sweep.
    MuiChip: {
      styleOverrides: {
        // Dedicated filled* slots (specificity 0-1-0) rather than compound
        // `root: &.MuiChip-filled*` (0-2-0) — so component-level `sx` colours
        // (e.g. the dashboard's data-viz category chips) still override the tint.
        root: {
          height: 22,
          fontSize: 11,
          fontWeight: 500,
          borderRadius: 999,
          letterSpacing: "0.01em",
        },
        sizeSmall: { height: 22 },
        label: { paddingLeft: 10, paddingRight: 10 },
        filledDefault: { backgroundColor: "#f3f3f0", color: "#2f2f2c" },
        filledSuccess: { backgroundColor: "#e4f4ec", color: "#1f4741" },
        filledWarning: { backgroundColor: "#faefd8", color: "#7d5a1a" },
        filledError: { backgroundColor: "#f8e4de", color: "#7a3020" },
        filledInfo: { backgroundColor: "#e8f2f6", color: "#18576e" },
      },
    },

    // -- Alert: DS "status banner" — outlined, soft brand tints --
    // Outlined by default (README: `<Alert variant="outlined">`). Four severities
    // only; each tint shares the Chip filled* text colours for consistency. Icon
    // top-aligned so multi-line banners read cleanly. NB: transient toasts
    // (contexts/AlertContext) intentionally stay variant="filled" — that's an
    // overlay pattern the DS does not define, not an inline banner.
    MuiAlert: {
      defaultProps: { variant: "outlined" },
      styleOverrides: {
        root: {
          borderRadius: 6,
          padding: "10px 14px",
          alignItems: "flex-start",
        },
        outlinedInfo: {
          borderColor: "#bbd6e0",
          backgroundColor: "#eef4f8",
          color: "#18576e",
        },
        outlinedSuccess: {
          borderColor: "#aadcc2",
          backgroundColor: "#e4f4ec",
          color: "#1f4741",
        },
        outlinedWarning: {
          borderColor: "#f0d999",
          backgroundColor: "#fdf6e4",
          color: "#7d5a1a",
        },
        outlinedError: {
          borderColor: "#f0c2b4",
          backgroundColor: "#fbeae3",
          color: "#7a3020",
        },
      },
    },

    // -- Tabs: sentence-case labels, thin teal underline, hairline rail --
    // The bottom rail lives on MuiTabs.root (not a wrapper Box). Selected label
    // is near-black ink (#2f2f2c) with only the 2px indicator carrying the teal
    // — the calm DS treatment, not MUI's teal-everything default.
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 40, borderBottom: "1px solid #e7e7e3" },
        indicator: { backgroundColor: "#295b54", height: 2 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 40,
          padding: "10px 16px",
          fontSize: 13,
          fontWeight: 500,
          textTransform: "none",
          color: "#6e6e66",
          "&.Mui-selected": { color: "#2f2f2c" },
        },
      },
    },

    // -- Cards & surfaces: flat, hairline border, 6px radius --
    // Card defaults to outlined (hairline over fill, no shadow at rest).
    MuiCard: {
      defaultProps: { elevation: 0, variant: "outlined" },
      styleOverrides: {
        root: { borderColor: "#e7e7e3", borderRadius: 6 },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: { padding: 20, "&:last-child": { paddingBottom: 20 } },
      },
    },
    // Paper: flat by default (elevation 0), separated by a hairline rather than
    // a shadow. Surfaces that float set their own elevation (overlays use 8/24,
    // floating panels set elevation explicitly) and keep their shadow + no border.
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: "none",
          // Hairline for flat CONTENT surfaces only. Exclude AppBar and the
          // permanent Drawer paper — both are elevation-0 Papers too, but they
          // carry their own single-edge border (bottom / right), so the 4-side
          // rule would double their borders and add stray top/side hairlines.
          "&.MuiPaper-elevation0:not(.MuiAppBar-root):not(.MuiDrawer-paper)": {
            border: "1px solid #e7e7e3",
          },
        },
        outlined: { borderColor: "#e7e7e3" },
      },
    },

    // -- Overlays: float above the page on a teal-tinted shadow, no hairline --
    // The brand book's three shadows: cards (hairline), menus/popovers (shadow-3),
    // modals/toasts (shadow-float). Overlays get radius lg (10) except dropdown
    // Menus which keep the 6px surface radius. Shadows are cool-neutral teal,
    // never black. DialogContent/Actions stay stock MUI (the DS leaves them).
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 10,
          boxShadow: "0 24px 64px -24px rgba(17,38,36,0.25)",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { fontSize: 18, fontWeight: 600, padding: "20px 24px 8px" },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          // 6px (not the DS doc's lg/10) so Popover matches its Menu twin — both
          // are anchored poppers. 10px is reserved for the Dialog modal. See
          // DESIGN-SYSTEM-EXTENSIONS.md "Overlays" for the rationale.
          borderRadius: 6,
          boxShadow:
            "0 8px 24px -8px rgba(17,38,36,0.12), 0 2px 6px rgba(17,38,36,0.04)",
          // Content padding for plain (free-form) popovers, so call sites need no
          // inline sx. Scoped out of Menus — their paper also carries
          // .MuiPopover-paper, but MenuList owns its own vertical padding.
          "&:not(.MuiMenu-paper)": { padding: 10 },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 6,
          boxShadow:
            "0 8px 24px -8px rgba(17,38,36,0.12), 0 2px 6px rgba(17,38,36,0.04)",
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "#1c1c1a",
          fontSize: 12,
          padding: "6px 10px",
          borderRadius: 4,
        },
        arrow: { color: "#1c1c1a" },
      },
    },

    // -- Table: uppercase overline head, per-row hairline, tabular-nums body --
    // Header is distinguished by TYPE (11px/500/uppercase/tracked ink500), not a
    // fill or a heavier rule — the row hairline and header underline are both the
    // 1px #e7e7e3 divider. Body cells recolour MUI's default hairline to the DS
    // divider and turn on tabular numerals. Padding set on root AND sizeSmall so
    // the DS 10x12 wins over MUI's small-size cell padding (tables use size small).
    MuiTableHead: {
      styleOverrides: {
        root: {
          "& .MuiTableCell-root": {
            fontSize: 11,
            fontWeight: 500,
            color: "#6e6e66",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: 13,
          padding: "10px 12px",
          borderBottomColor: "#e7e7e3",
        },
        sizeSmall: { padding: "10px 12px" },
        body: { fontVariantNumeric: "tabular-nums" },
      },
    },

    // -- DataGrid: match the Table theme (DS doesn't spec it; owner asked to align) --
    // Uppercase overline column headers, #e7e7e3 row/header hairline via the v7
    // CSS var, 13px tabular-nums body, no outer border, no vertical separators
    // (Tables have none; column resize is disabled on every grid anyway).
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: 0,
          fontSize: 13,
          fontVariantNumeric: "tabular-nums",
          "--DataGrid-rowBorderColor": "#e7e7e3",
        },
        columnHeaderTitle: {
          fontSize: 11,
          fontWeight: 500,
          color: "#6e6e66",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
        },
        columnSeparator: { visibility: "hidden" },
        withBorderColor: { borderColor: "#e7e7e3" },
      },
    },

    // -- Small primitives: Switch / LinearProgress / Divider / Link --
    // Switch: flat DS toggle — off ink300, on brandDeep, shadowless 16px thumb.
    // LinearProgress: 6px pill, ink100 track, teal bar. Divider: DS hairline.
    // Link: teal, underline on hover only, weight 500 (breadcrumbs override this
    // with their own muted treatment; the default is for inline content links).
    MuiSwitch: {
      styleOverrides: {
        root: { width: 36, height: 20, padding: 0 },
        switchBase: {
          padding: 2,
          "&.Mui-checked": {
            transform: "translateX(16px)",
            // Keep the thumb white when ON. MUI's default colours the checked
            // thumb with primary.main, which equals our brandDeep track → the
            // thumb vanishes. The DS preview keeps a white thumb; the DS theme
            // omitted this, so we set it (contrast against the green track).
            color: "#fff",
            "& + .MuiSwitch-track": {
              backgroundColor: "#295b54",
              opacity: 1,
            },
          },
        },
        // size="small" (used by the app's toggles) forces switchBase padding to
        // 4px via a more specific rule, making the switchBase 24px tall inside
        // our 20px root → the thumb bottom-aligns. Re-assert padding 2 for the
        // small size so 16px thumb + 2×2 padding = 20px and the thumb centres.
        sizeSmall: { "& .MuiSwitch-switchBase": { padding: 2 } },
        thumb: { width: 16, height: 16, boxShadow: "none" },
        track: { borderRadius: 999, backgroundColor: "#cfcfc9", opacity: 1 },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { height: 6, borderRadius: 999, backgroundColor: "#f3f3f0" },
        bar: { backgroundColor: "#2a81a0" },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: "#e7e7e3" } },
    },
    MuiLink: {
      defaultProps: { underline: "hover", color: "#2a81a0" },
      styleOverrides: { root: { fontWeight: 500 } },
    },

    MuiDatePicker: {
      defaultProps: {
        format: "DD/MM/YYYY",
        slots: { openPickerIcon: OpenPickerCalendarIcon },
      },
    },
    MuiDateField: {
      defaultProps: {
        format: "DD/MM/YYYY",
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: "inherit" },
      styleOverrides: {
        root: {
          backgroundColor: "rgba(250,250,248,0.85)",
          backdropFilter: "blur(8px)",
          borderBottom: "1px solid #e7e7e3",
          color: "#2f2f2c",
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: 56,
          "@media (min-width:600px)": { minHeight: 56 },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: "1px solid #e7e7e3",
          backgroundColor: "#ffffff",
        },
      },
    },

    // -- Nav items: rounded, inset, subtle neutral selected fill (no teal) --
    // Selected = ink100 fill + ink700/500 text (calm, per DS — no teal accent,
    // no left-indicator; the brand book forbids coloured left borders). Hover is
    // the same ink100 fill. Icon inherits the item's text colour.
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          margin: "1px 8px",
          padding: "8px 10px",
          color: "#6e6e66",
          "&:hover": { backgroundColor: "#f3f3f0" },
          "&.Mui-selected": {
            backgroundColor: "#f3f3f0",
            color: "#2f2f2c",
            fontWeight: 500,
          },
          "&.Mui-selected:hover": { backgroundColor: "#f3f3f0" },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: { root: { minWidth: 32, color: "inherit" } },
    },
    MuiListItemText: {
      styleOverrides: { primary: { fontSize: 14, fontWeight: "inherit" } },
    },
  },
});

export default theme;
